/**
 * Un nom pour un enregistrement que personne n'a nommé.
 *
 * # Pourquoi tirer au sort plutôt que dater
 *
 * Parce que le nom sert à **distinguer**, et que la date ne distingue rien :
 * dans « Mes récitals », chaque fiche porte déjà son moment, et trois
 * « Enregistrement du 19 septembre » à la suite se ressemblent exactement
 * autant que trois lignes vides. « Barcarolle têtue » se retrouve d'un coup
 * d'œil, se cite à quelqu'un, et se renomme quand la prise mérite mieux.
 *
 * # Pourquoi ces mots-là
 *
 * Des formes brèves du répertoire, celles qu'on écrit en une séance, et un
 * qualificatif qui pourrait décrire un essai. Rien qui parle du moment de la
 * journée : un « nocturne matinal » se remarquerait, et pas pour la bonne
 * raison.
 *
 * **Toutes les formes sont féminines**, et c'est la seule raison pour laquelle
 * les deux listes se combinent sans accord à faire. Une forme masculine
 * ajoutée ici (« impromptu », « prélude ») donnerait « Prélude feutrée » : il
 * faudrait alors porter les deux genres de chaque adjectif, pour un nom jetable.
 */

const FORMES: readonly string[] = [
  "Esquisse",
  "Bagatelle",
  "Ballade",
  "Fantaisie",
  "Rhapsodie",
  "Sérénade",
  "Étude",
  "Cadence",
  "Romance",
  "Berceuse",
  "Barcarolle",
  "Tarentelle",
];

const HUMEURS: readonly string[] = [
  "brève",
  "libre",
  "sereine",
  "vagabonde",
  "feutrée",
  "limpide",
  "discrète",
  "obstinée",
  "légère",
  "rêveuse",
  "fugace",
  "têtue",
  "buissonnière",
  "hâtive",
];

function pick(words: readonly string[]): string {
  return words[Math.floor(Math.random() * words.length)];
}

/** « Barcarolle têtue ». Deux tirages indépendants, 168 noms possibles. */
export function randomRecitalTitle(): string {
  return `${pick(FORMES)} ${pick(HUMEURS)}`;
}

/** Les deux listes, pour le banc : rien d'autre ne doit en sortir. */
export const RECITAL_NAME_WORDS = { formes: FORMES, humeurs: HUMEURS } as const;
